import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';

/**
 * Money crosses the wire as decimal STRINGS, and stays that way here.
 *
 * A single LLM call costs a fraction of a cent; parsing these into JS numbers and summing them
 * in the browser reintroduces exactly the drift the Decimal columns exist to prevent. Totals are
 * computed server-side — the UI's job is to display, not to add up.
 */
export interface LlmUsageRow {
  id: number;
  request_id: string;
  tenant_id: number | null;
  tenant_code: string | null;
  tenant?: { tenant_name: string | null; tenant_subdomain: string | null } | null;
  session_code: string | null;
  pd_id: string | null;
  agent_id: number | null;
  task_code: string;
  provider: string;
  model_name: string;
  model?: { display_name: string } | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cached_tokens: number | null;
  total_tokens: number | null;
  total_cost_usd: string;
  cost_is_priced: boolean;
  status: string;
  source: string;
  processing_time_ms: number | null;
  request_timestamp: string;
  error_code?: string | null;
}

export interface LlmUsageListResponse extends ApiEnvelope<LlmUsageRow[]> {
  meta: { total: number; page: number; limit: number; pages: number };
}

export interface LlmDashboardTotals {
  calls: number;
  pds: number;
  failed_calls: number;
  unknown_billed_calls: number;
  input_tokens: number;
  output_tokens: number;
  cached_tokens: number;
  total_tokens: number;
  total_cost_usd: string;
  total_cost_inr: string;
  avg_cost_per_pd_usd: string;
  avg_cost_per_call_usd: string;
  avg_tokens_per_pd: number;
}

export interface LlmTrendPoint {
  date: string;
  calls: number;
  pds: number;
  total_tokens: number;
  total_cost_usd: string;
  avg_cost_per_pd_usd: string;
}

export interface LlmTenantCostRow {
  tenant_id: number | null;
  tenant_name: string;
  tenant_subdomain: string | null;
  calls: number;
  pds: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  total_cost_usd: string;
  avg_cost_per_pd_usd: string;
}

export interface LlmModelUsageRow {
  model_name: string;
  calls: number;
  total_tokens: number;
  total_cost_usd: string;
  cost_pct: string;
  token_pct: string;
  call_pct: string;
  avg_cost_per_call_usd: string;
}

/** Full record for one call — the drawer. Includes the cost split the table has no room for. */
export interface LlmUsageDetail extends LlmUsageRow {
  input_cost_usd: string;
  output_cost_usd: string;
  cached_cost_usd: string;
  input_price_per_1m: string | null;
  output_price_per_1m: string | null;
  fx_rate_usd_inr: string | null;
  total_cost_inr: string | null;
  reasoning_tokens: number | null;
  model_version: string | null;
  workflow_id: number | null;
  flow_definition_id: string | null;
  country: string | null;
  kyc_type: string | null;
  attempt: number;
  response_timestamp: string | null;
  error_message?: string | null;
}

export interface LlmPdCostCall {
  id: number;
  task_code: string;
  model_name: string;
  model_display_name: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cached_tokens: number | null;
  total_tokens: number | null;
  total_cost_usd: string;
  cost_is_priced: boolean;
  status: string;
  processing_time_ms: number | null;
  request_timestamp: string;
}

export interface LlmPdListRow {
  session_code: string;
  pd_id: string | null;
  tenant_id: number | null;
  tenant_name: string;
  agent_id: number | null;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  total_cost_usd: string;
  last_at: string | null;
}

export interface LlmPdListResponse extends ApiEnvelope<LlmPdListRow[]> {
  meta: { total: number; page: number; limit: number; pages: number };
}

export interface LlmAgentUsageRow {
  agent_id: number | null;
  agent_name: string;
  calls: number;
  pds: number;
  total_tokens: number;
  total_cost_usd: string;
  avg_cost_per_pd_usd: string;
}

export interface LlmWorkflowUsageRow {
  flow_definition_id: string | null;
  workflow_name: string;
  calls: number;
  pds: number;
  total_tokens: number;
  total_cost_usd: string;
  avg_cost_per_pd_usd: string;
}

export interface LlmPdCost {
  pd: {
    session_code: string;
    pd_id: string | null;
    pd_session_id: number | null;
    tenant_id: number | null;
    tenant_code: string | null;
    agent_id: number | null;
  };
  summary: { calls: number; input_tokens: number; output_tokens: number; total_tokens: number; total_cost_usd: string };
  calls: LlmPdCostCall[];
}

export interface LlmMarginRow {
  tenant_id: number;
  tenant_name: string;
  tenant_subdomain: string | null;
  billed_pds: number;
  llm_calls: number;
  revenue_inr: string;
  covered_by_plan_inr: string;
  ai_cost_inr: string;
  margin_inr: string;
  margin_pct: string;
  ai_cost_pct_of_revenue: string;
  unpriced_calls: number;
  /** False when some calls had no price, or when no calls were recorded — margin is a ceiling. */
  cost_is_complete: boolean;
  currency_mismatch: boolean;
}

export interface LlmMarginResponse {
  data: LlmMarginRow[];
  unattributed_ai_cost_inr: string;
}

export interface LlmPricingRow {
  id: number;
  model_id: number;
  model?: { provider: string; model_name: string; display_name: string };
  input_price_per_1m: string;
  output_price_per_1m: string;
  cached_input_price_per_1m: string | null;
  currency: string;
  effective_from: string;
  effective_to: string | null;
  is_active: boolean;
  notes: string | null;
}

export interface LlmUsageFilters {
  page?: number;
  limit?: number;
  tenant_id?: number;
  session_code?: string;
  pd_id?: string;
  agent_id?: number;
  model_id?: number;
  model_name?: string;
  provider?: string;
  task_code?: string;
  status?: string;
  source?: string;
  from?: string;
  to?: string;
  /** PD list order: 'cost' (dearest first, default) or 'recent' (latest call first). */
  sort?: 'cost' | 'recent';
}

@Injectable({ providedIn: 'root' })
export class LlmUsageApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  private params(filters: LlmUsageFilters): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    }
    return params;
  }

  list(filters: LlmUsageFilters = {}): Observable<LlmUsageListResponse> {
    return this.http.get<LlmUsageListResponse>(`${this.base}/llm-usage`, { params: this.params(filters) });
  }

  get(id: number): Observable<ApiEnvelope<LlmUsageDetail>> {
    return this.http.get<ApiEnvelope<LlmUsageDetail>>(`${this.base}/llm-usage/${id}`);
  }

  dashboard(filters: LlmUsageFilters = {}): Observable<ApiEnvelope<{ totals: LlmDashboardTotals }>> {
    return this.http.get<ApiEnvelope<{ totals: LlmDashboardTotals }>>(`${this.base}/llm-usage/dashboard`, {
      params: this.params(filters),
    });
  }

  trend(filters: LlmUsageFilters = {}): Observable<ApiEnvelope<LlmTrendPoint[]>> {
    return this.http.get<ApiEnvelope<LlmTrendPoint[]>>(`${this.base}/llm-usage/trend`, {
      params: this.params(filters),
    });
  }

  tenants(filters: LlmUsageFilters = {}): Observable<ApiEnvelope<LlmTenantCostRow[]>> {
    return this.http.get<ApiEnvelope<LlmTenantCostRow[]>>(`${this.base}/llm-usage/tenants`, {
      params: this.params(filters),
    });
  }

  models(filters: LlmUsageFilters = {}): Observable<ApiEnvelope<LlmModelUsageRow[]>> {
    return this.http.get<ApiEnvelope<LlmModelUsageRow[]>>(`${this.base}/llm-usage/models`, {
      params: this.params(filters),
    });
  }

  pdList(filters: LlmUsageFilters = {}): Observable<LlmPdListResponse> {
    return this.http.get<LlmPdListResponse>(`${this.base}/llm-usage/pds`, { params: this.params(filters) });
  }

  agents(filters: LlmUsageFilters = {}): Observable<ApiEnvelope<LlmAgentUsageRow[]>> {
    return this.http.get<ApiEnvelope<LlmAgentUsageRow[]>>(`${this.base}/llm-usage/agents`, {
      params: this.params(filters),
    });
  }

  workflows(filters: LlmUsageFilters = {}): Observable<ApiEnvelope<LlmWorkflowUsageRow[]>> {
    return this.http.get<ApiEnvelope<LlmWorkflowUsageRow[]>>(`${this.base}/llm-usage/workflows`, {
      params: this.params(filters),
    });
  }

  pdCost(sessionCode: string): Observable<ApiEnvelope<LlmPdCost>> {
    return this.http.get<ApiEnvelope<LlmPdCost>>(`${this.base}/llm-usage/pd/${encodeURIComponent(sessionCode)}`);
  }

  margin(filters: LlmUsageFilters = {}): Observable<ApiEnvelope<LlmMarginResponse>> {
    return this.http.get<ApiEnvelope<LlmMarginResponse>>(`${this.base}/llm-usage/margin`, {
      params: this.params(filters),
    });
  }

  listPricing(): Observable<ApiEnvelope<LlmPricingRow[]>> {
    return this.http.get<ApiEnvelope<LlmPricingRow[]>>(`${this.base}/llm-usage/pricing`);
  }

  createPricing(body: Record<string, unknown>): Observable<ApiEnvelope<LlmPricingRow>> {
    return this.http.post<ApiEnvelope<LlmPricingRow>>(`${this.base}/llm-usage/pricing`, body);
  }

  updatePricing(id: number, body: Record<string, unknown>): Observable<ApiEnvelope<LlmPricingRow>> {
    return this.http.put<ApiEnvelope<LlmPricingRow>>(`${this.base}/llm-usage/pricing/${id}`, body);
  }

  deletePricing(id: number): Observable<ApiEnvelope<void>> {
    return this.http.delete<ApiEnvelope<void>>(`${this.base}/llm-usage/pricing/${id}`);
  }
}
